import { z } from 'zod';

import { CreateTransactionShapeStrict } from '@dealstack/shared/lib/common/zod/domain/transaction/transactions.zod';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import type { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { PersonType, type DataSecurityTct } from '@dealstack/shared/lib/generated';
import { isNomineeIssueSplitEligible } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/useTransactionFieldsVisibility';

/**
 * CAP-1975: the nominee legal/beneficial inputs the create-transaction drawer adds on top of the
 * shared transaction shape. They are drawer-only — the payload carries them as the paired-issue
 * `nomineeId` / `beneficialBuyerId` pair rather than as transaction columns.
 */
const NomineeInterestShape = z.object({
	/** On: legal title and beneficial interest both go to the buyer. Off: `nomineeId` holds legal title. */
	legalSameAsBeneficial: z.boolean().default(true),
	nomineeId: z.string().nullable().optional(),
});

/**
 * CAP-1975: the create-transaction drawer's form shape.
 *
 * On an issue that qualifies for the legal/beneficial split, switching "Same as beneficial interest?"
 * off routes the submit through `/cap/transaction/paired-issue`, which needs a legal-title holder
 * distinct from the beneficial buyer — so the nominee is required and rejected when it equals the
 * buyer. Both are field-level errors on `nomineeId`.
 *
 * The buyer's holding constrains the BENEFICIAL buyer only. A nominee is exempt from the
 * bought-for-inside-a-programme rule (it holds bare legal title, so it holds for nobody), which is
 * what lets both titles sit under one holding.
 *
 * Every rule is gated on the same predicate that renders those inputs
 * (`isNomineeIssueSplitEligible`) and reported against a field the user can act on, so a form can
 * never be blocked by an error on a control it does not show. That second half is load-bearing: a
 * zod issue whose `path` names a hidden control blocks submission with nothing on screen, which
 * reads to the user as a dead Create button.
 */
export const CreateTransactionFormShape = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	securities?: DataSecurityTct[],
	persons?: { value: string; type: PersonType }[]
) =>
	CreateTransactionShapeStrict()
		.and(NomineeInterestShape)
		.superRefine((data, ctx) => {
			// A legal person holding inside a programme must name the natural person it holds for — the
			// backend rejects the leg otherwise ("Destination person is legal person but no
			// boughtForPersonId provided"). Gated on the PROGRAMME being set, exactly as the backend
			// gates it, so a rail with no programme is never blocked on a control the drawer hides.
			// This applies to every transaction, not just a nominee split, so it sits above the
			// nominee-eligibility return below.
			const isLegalPerson = (id?: string | null) =>
				!!id && persons?.find((p) => p.value === id)?.type === PersonType.LEGAL;

			if (data.toMipId && isLegalPerson(data.destinationId) && !data.boughtForId) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: tCapManage('transactions.drawer.bought_for.validation.required'),
					// Reported against the BUYER'S HOLDING, not bought-for. Bought-for renders only
					// when the buyer is a legal person that HAS natural persons behind it
					// (useTransactionPartyPivRelation's hideHoldsFor); on a corporate nominee with
					// none, both of its render gates are false and an issue on that path has nowhere
					// to display, so Create did nothing and said nothing. The holding is the control
					// the user just set and the one the message offers to clear, so it is always on
					// screen when this rule can fire.
					path: ['toMipId'],
				});
			}

			const security = securities?.find((candidate) => candidate.id === data.securityId);
			if (!isNomineeIssueSplitEligible(data.type, security) || data.legalSameAsBeneficial) {
				return;
			}

			if (!data.nomineeId) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: tCapManage('transactions.drawer.nominee.validation.required'),
					path: ['nomineeId'],
				});
				return;
			}

			if (data.nomineeId === data.destinationId) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: tCapManage('transactions.drawer.nominee.validation.same_as_buyer'),
					path: ['nomineeId'],
				});
			}
		});

export type CreateTransactionFormDto = z.infer<ReturnType<typeof CreateTransactionFormShape>>;
